import {
  isTemplateCreateOperation,
  isTemplateHideOperation,
} from '@festina/collcalc-general-types/collcalc'
import {
  type AnnulOperation,
  isLibraryInOut,
  isRegisteredTenantCreateOperation,
  type ParameterDefinitionCreateOperation,
  type RegisteredTenantCreateOperation,
} from '@festina/configuration-general-types/configuration'
import {
  LAYER_ID_TENANT_SEPARATOR,
  LayerKey,
  type OperationLayerMap,
  resolveTopologicalPath,
  type Tree,
} from '@festina-life/api'
import {
  assertNotEmpty,
  type NonEmptyArray,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import {
  addLeafToTree,
  type TreeLeafData,
  type TreeNodeData,
} from '@festina-life/ui/listsAndTables'

import type { FilterStatus } from '../../components/configurationItemsTree/types'
import type { useTranslateLayer } from '../../hooks/useTranslateLayer'
import type { EnumDefinition } from '../../uiModels/enumLiterals/types'
import { isHideOnlyGlMap } from '../../uiModels/glMapOperations/helpers'
import type { IndexedGlMapOperations } from '../../uiModels/glMapOperations/types'
import { isParameterValueEntityOperation } from '../../uiModels/parameterOperations/helpers'
import type {
  IndexedParameterDefinitionCreateOperations,
  IndexedParameterValueOperations,
  ParameterValueOperation,
} from '../../uiModels/parameterOperations/types'
import type { IndexedReconciliationSpecOperations } from '../../uiModels/reconciliationSpecOperations/types'
import type { IndexedTemplateOperations } from '../../uiModels/templateOperations/types'
import {
  isApproved,
  type OperationWithSourceInfo,
  type UserActionData,
} from '../../uiModels/types'
import {
  type ActiveConfigurationItem,
  ConfigurationLeafTags,
  type ConfigurationTreeLeaf,
} from './types'

type ActiveItemId = {
  itemId: string
  layer: LayerKey
  block: string
}

const parameterDefinitionVersionLeaf = ({
  operations,
  fallbackPackageName,
  activeParameterDefinitionId,
}: {
  operations: IndexedParameterOperations
  fallbackPackageName: Optional<string>
  activeParameterDefinitionId: Optional<ActiveItemId>
}): ConfigurationTreeLeaf => {
  const fallbackEntityOperation = operations.values
    ?.filter(notEmpty)
    .map((configurationOperation) => configurationOperation.internalOperation)
    .find(isParameterValueEntityOperation)

  const tags = []

  const isExtrinsic = operations.definitions.operations.some(
    (operation) => operation.internalOperation.entity?.isExtrinsic,
  )

  if (
    operations.definitions.isDraft ||
    !!operations.values?.some((value) => value.isDraft)
  ) {
    tags.push(ConfigurationLeafTags.draft)
  }

  if (isExtrinsic) {
    tags.push(ConfigurationLeafTags.extrinsic)
  }
  const layer =
    operations.definitions.operations.at(0)?.layer ??
    operations.values?.at(0)?.layer

  const block =
    operations.definitions.operations.at(0)?.block ??
    operations.values?.at(0)?.block

  assertNotEmpty(layer)
  assertNotEmpty(block)

  return {
    id: operations.definitions.parameterId,
    blocks: [block],
    layer,
    description:
      operations.definitions.lastParameterEntity?.parameterType ??
      fallbackEntityOperation?.entity?.parameterType ??
      null,
    packageName: operations.definitions.packageName ?? fallbackPackageName,
    tags,
    type: 'parameterDefinition',
    isActive:
      !!activeParameterDefinitionId &&
      operations.definitions.parameterId ===
        activeParameterDefinitionId?.itemId &&
      (operations.definitions.operations.some((operation) =>
        operation.layer.equals(activeParameterDefinitionId.layer),
      ) ||
        !!operations.values?.some((value) =>
          value.layer.equals(activeParameterDefinitionId?.layer),
        )) &&
      (operations.definitions.operations.some(
        (operation) => operation.block === activeParameterDefinitionId?.block,
      ) ||
        !!operations.values?.some(
          (value) => value.block === activeParameterDefinitionId?.block,
        )),
  }
}

const enumLeaf = ({
  definition,
  fallbackPackageName,
  activeEnumId,
  parentLayer,
  parentBlock,
}: {
  definition: EnumDefinition
  fallbackPackageName: Optional<string>
  activeEnumId: Optional<ActiveItemId>
  parentLayer: LayerKey
  parentBlock: string
}): ConfigurationTreeLeaf => ({
  id: definition.name,
  layer: parentLayer,
  blocks: [parentBlock],
  description: null,
  packageName: fallbackPackageName,
  tags:
    definition.isDraft ||
    definition.literals.some(
      (literal) => literal.type === 'configured' && literal.isDraft,
    )
      ? [ConfigurationLeafTags.draft]
      : [],
  type: 'enumDefinition',
  isActive:
    !!activeEnumId &&
    definition.name === activeEnumId?.itemId &&
    parentLayer.equals(activeEnumId.layer) &&
    parentBlock === activeEnumId?.block,
})

const tenantLeaf = ({
  item,
  fallbackPackageName,
  activeTenantId,
}: {
  item: OperationWithSourceInfo<RegisteredTenantCreateOperation>
  fallbackPackageName: Optional<string>
  activeTenantId: Optional<ActiveItemId>
}): ConfigurationTreeLeaf => {
  assertNotEmpty(item.internalOperation.registeredTenant?.tenantId)

  return {
    id: item.internalOperation.registeredTenant.tenantId,
    layer: item.layer,
    blocks: [item.block],
    description: null,
    packageName: fallbackPackageName,
    tags: item.isDraft ? [ConfigurationLeafTags.draft] : [],
    type: 'tenant',
    isActive:
      !!activeTenantId &&
      item.internalOperation.registeredTenant.tenantId ===
        activeTenantId.itemId &&
      item.layer.equals(activeTenantId.layer) &&
      item.block === activeTenantId?.block,
  }
}

const templateLeaf = ({
  createOperations,
  fallbackPackageName,
  activeTemplateId,
}: {
  createOperations: IndexedTemplateOperations
  fallbackPackageName: Optional<string>
  activeTemplateId: Optional<ActiveItemId>
}): ConfigurationTreeLeaf => {
  const modelTypeName = createOperations.lastEntity?.modelTypeName
  const layer = createOperations.operations.at(0)?.layer
  const block = createOperations.operations.at(0)?.block
  assertNotEmpty(layer)
  assertNotEmpty(block)

  return {
    id: createOperations.templateId,
    layer,
    blocks: [block],
    description: modelTypeName,
    packageName:
      getPackageNameFromIndexedTemplateCreateOperations(createOperations) ??
      fallbackPackageName,
    tags: createOperations.isDraft ? [ConfigurationLeafTags.draft] : [],
    type: isLibraryInOut(createOperations.lastEntity) ? 'library' : 'template',
    isActive:
      !!activeTemplateId &&
      activeTemplateId.itemId === createOperations.templateId &&
      layer.equals(activeTemplateId.layer) &&
      block === activeTemplateId?.block,
  }
}

const glMapLeaf = ({
  createOperations,
  fallbackPackageName,
  activeMappingId,
}: {
  createOperations: IndexedGlMapOperations
  fallbackPackageName: Optional<string>
  activeMappingId: Optional<ActiveItemId>
}): ConfigurationTreeLeaf => {
  const layer = createOperations.operations.at(0)?.layer
  const block = createOperations.operations.at(0)?.block
  assertNotEmpty(layer)
  assertNotEmpty(block)

  return {
    id: createOperations.mappingId,
    layer,
    blocks: [block],
    description: null,
    packageName: fallbackPackageName,
    tags: createOperations.isDraft ? [ConfigurationLeafTags.draft] : [],
    type: 'glMap',
    isActive:
      !!activeMappingId &&
      activeMappingId.itemId === createOperations.mappingId &&
      layer.equals(activeMappingId.layer) &&
      block === activeMappingId.block,
  }
}

const reconciliationSpecLeaf = ({
  createOperations,
  fallbackPackageName,
  activeSpecId,
}: {
  createOperations: IndexedReconciliationSpecOperations
  fallbackPackageName: Optional<string>
  activeSpecId: Optional<ActiveItemId>
}): ConfigurationTreeLeaf => {
  const layer = createOperations.operations.at(0)?.layer
  const block = createOperations.operations.at(0)?.block
  assertNotEmpty(layer)
  assertNotEmpty(block)

  return {
    id: createOperations.specId,
    layer,
    blocks: [block],
    description: null,
    packageName: fallbackPackageName,
    tags: createOperations.isDraft ? [ConfigurationLeafTags.draft] : [],
    type: 'reconciliationSpec',
    isActive:
      !!activeSpecId &&
      activeSpecId.itemId === createOperations.specId &&
      layer.equals(activeSpecId.layer) &&
      block === activeSpecId.block,
  }
}

const getPackageNameFromIndexedTemplateCreateOperations = (
  createOperations: IndexedTemplateOperations,
): Optional<string> => {
  const lastOperation = createOperations.operations.findLast(Boolean)

  return isTemplateCreateOperation(lastOperation?.internalOperation)
    ? lastOperation?.internalOperation.entity?.packageName
    : lastOperation?.internalOperation.packageName
}

export type IndexedParameterOperations = {
  definitions: IndexedParameterDefinitionCreateOperations
  values: Optional<IndexedParameterValueOperations>
}

type ConfigurationOperation =
  | IndexedParameterOperations
  | IndexedTemplateOperations
  | IndexedGlMapOperations
  | IndexedReconciliationSpecOperations
  | EnumDefinition
  | OperationWithSourceInfo<RegisteredTenantCreateOperation>

const isIndexedGlMapOperations = (
  item: ConfigurationOperation,
): item is IndexedGlMapOperations => 'mappingId' in item && 'operations' in item

const isIndexedReconciliationSpecOperations = (
  item: ConfigurationOperation,
): item is IndexedReconciliationSpecOperations =>
  'specId' in item && 'operations' in item

const isIndexedParameterCreateOperations = (
  item: ConfigurationOperation,
): item is IndexedParameterOperations =>
  'definitions' in item && 'values' in item

const isEnumDefinition = (
  item: ConfigurationOperation,
): item is EnumDefinition =>
  'name' in item && 'internalOperation' in item && 'literals' in item

const isRegisteredTenantCreateOperationWithSourceInfo = (
  item: ConfigurationOperation,
): item is OperationWithSourceInfo<RegisteredTenantCreateOperation> =>
  'internalOperation' in item &&
  isRegisteredTenantCreateOperation(item.internalOperation)

/**
 * Recursively sorts tree nodes so that layer nodes (path including ':')
 * appear before non-layer nodes, with alphabetical sorting within each group.
 */
const sortTreeNodes = (trees: TreeNodeData<ConfigurationTreeLeaf>[]): void => {
  trees.sort((a, b) => {
    const aIsLayer = a.path.at(-1)?.includes(LAYER_ID_TENANT_SEPARATOR) ?? false
    const bIsLayer = b.path.at(-1)?.includes(LAYER_ID_TENANT_SEPARATOR) ?? false
    if (aIsLayer !== bIsLayer) {
      return aIsLayer ? -1 : 1
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
  for (const node of trees) {
    sortTreeNodes(node.nodes)
  }
}

/**
 * The tree is built per-block, so the same item (same id) can appear as
 * multiple leaves when it exists in more than one block. This pass
 * deduplicates leaves within every node, merging their blocks, tags, and
 * active state into a single entry.
 */
const mergeLeavesByIdInTree = (
  trees: TreeNodeData<ConfigurationTreeLeaf>[],
): void => {
  for (const node of trees) {
    const mergedLeaves = new Map<string, ConfigurationTreeLeaf>()

    for (const leaf of node.leaves) {
      const existing = mergedLeaves.get(leaf.id)
      if (existing && existing.type === leaf.type) {
        existing.blocks = [...new Set([...existing.blocks, ...leaf.blocks])]
        existing.tags = [...new Set([...existing.tags, ...leaf.tags])]
        existing.isActive = existing.isActive || leaf.isActive
      } else {
        mergedLeaves.set(leaf.id, { ...leaf })
      }
    }

    node.leaves = Array.from(mergedLeaves.values())
    mergeLeavesByIdInTree(node.nodes)
  }
}

const ensureNodeInTree = <Leaf extends TreeLeafData>({
  tree,
  atPath,
  nameFromPathPart,
  currentPath = [],
}: {
  tree: TreeNodeData<Leaf>[]
  atPath: string
  nameFromPathPart: (part: string) => string
  currentPath?: string[]
}) => {
  const [nextPathPart, ...rest] = atPath.split('.')
  const targetPath = [
    ...currentPath,
    nextPathPart,
  ] as unknown as NonEmptyArray<string>
  const targetPathAsString = targetPath.join('.')
  let targetNode = tree.find(
    (node) => node.path.join('.') === targetPathAsString,
  )

  if (!targetNode) {
    targetNode = {
      path: targetPath,
      name: nameFromPathPart(nextPathPart),
      leaves: [],
      nodes: [],
      tags: [],
    }
    tree.push(targetNode)
  }

  if (rest.length) {
    ensureNodeInTree({
      tree: targetNode.nodes,
      atPath: rest.join('.'),
      nameFromPathPart,
      currentPath: targetPath,
    })
  }
}

export const fallbackPackageNameForLayer = ({
  layer,
  packageName,
}: {
  layer: LayerKey
  packageName: Optional<string>
}) => {
  const layerName = layer.layerId ?? ''

  return layerName && packageName ? `${layerName}.${packageName}` : packageName
}

export const createLayerConfigurationItemsTree = ({
  allBlockItems,
  fallbackPackageName,
  parametersPackageName,
  enumsPackageName,
  parameterDefinitionId,
  templateId,
  registeredTenantId,
  enumId,
  mappingId,
  specId,
  tenantsPackageName,
  glMapsPackageName,
  reconciliationSpecsPackageName,
  translateLayer,
  layerTopology,
  shouldIncludeLayers,
  shouldShowEmptyNodes,
}: {
  allBlockItems: {
    block: string
    items: OperationLayerMap<ConfigurationOperation[]>
  }[]
  templateId?: Optional<ActiveItemId>
  enumId?: Optional<ActiveItemId>
  registeredTenantId?: Optional<ActiveItemId>
  parameterDefinitionId?: Optional<ActiveItemId>
  mappingId?: Optional<ActiveItemId>
  specId?: Optional<ActiveItemId>
  fallbackPackageName: Optional<string>
  parametersPackageName: Optional<string>
  tenantsPackageName: Optional<string>
  glMapsPackageName: Optional<string>
  reconciliationSpecsPackageName: Optional<string>
  enumsPackageName: Optional<string>
  translateLayer: ReturnType<typeof useTranslateLayer>
  layerTopology: Optional<Tree>
  shouldIncludeLayers: Optional<boolean>
  shouldShowEmptyNodes?: Optional<boolean>
}): TreeNodeData<ConfigurationTreeLeaf>[] => {
  const trees: TreeNodeData<ConfigurationTreeLeaf>[] = []

  const nameFromPathPart = (part: string) =>
    shouldIncludeLayers && part.includes(LAYER_ID_TENANT_SEPARATOR)
      ? translateLayer(new LayerKey(part))
      : part

  for (const { block, items } of allBlockItems) {
    Array.from(items.entries()).forEach(([layer, itemsForLayer]) => {
      const layerName = layer.layerId ?? ''

      const prefixedFallbackPackageName = fallbackPackageNameForLayer({
        layer,
        packageName: fallbackPackageName,
      })
      const prefixedParametersPackageName = fallbackPackageNameForLayer({
        layer,
        packageName: parametersPackageName,
      })
      const prefixedEnumsPackageName = fallbackPackageNameForLayer({
        layer,
        packageName: enumsPackageName,
      })
      const prefixedTenantsPackageName = fallbackPackageNameForLayer({
        layer,
        packageName: tenantsPackageName,
      })
      const prefixedGlMapsPackageName = fallbackPackageNameForLayer({
        layer,
        packageName: glMapsPackageName,
      })
      const prefixedReconciliationSpecsPackageName =
        fallbackPackageNameForLayer({
          layer,
          packageName: reconciliationSpecsPackageName,
        })

      if (
        shouldIncludeLayers &&
        shouldShowEmptyNodes &&
        itemsForLayer.length === 0
      ) {
        const topologicalPath = resolveTopologicalPath(layer, layerTopology)
        const layerPathStr = topologicalPath
          .map((layerKey) => layerKey.toString())
          .join('.')
        ensureNodeInTree({
          tree: trees,
          atPath: layerPathStr,
          nameFromPathPart,
        })
        return
      }

      itemsForLayer.forEach((item) => {
        let leaf: ConfigurationTreeLeaf

        if (isIndexedParameterCreateOperations(item)) {
          leaf = parameterDefinitionVersionLeaf({
            operations: item,
            fallbackPackageName: prefixedParametersPackageName,
            activeParameterDefinitionId: parameterDefinitionId,
          })
        } else if (isEnumDefinition(item)) {
          leaf = enumLeaf({
            definition: item,
            fallbackPackageName: prefixedEnumsPackageName,
            activeEnumId: enumId,
            parentLayer: layer,
            parentBlock: block,
          })
        } else if (isRegisteredTenantCreateOperationWithSourceInfo(item)) {
          leaf = tenantLeaf({
            item,
            fallbackPackageName: prefixedTenantsPackageName,
            activeTenantId: registeredTenantId,
          })
        } else if (isIndexedGlMapOperations(item)) {
          leaf = glMapLeaf({
            createOperations: item,
            fallbackPackageName: prefixedGlMapsPackageName,
            activeMappingId: mappingId,
          })
        } else if (isIndexedReconciliationSpecOperations(item)) {
          leaf = reconciliationSpecLeaf({
            createOperations: item,
            fallbackPackageName: prefixedReconciliationSpecsPackageName,
            activeSpecId: specId,
          })
        } else {
          leaf = templateLeaf({
            createOperations: item,
            fallbackPackageName: prefixedFallbackPackageName,
            activeTemplateId: templateId,
          })
        }

        const topologicalPath = resolveTopologicalPath(layer, layerTopology)
        const layerPathStr = topologicalPath.map((k) => k.toString()).join('.')

        const strippedPackageName = sanitizePackageNameForLayer({
          packageName: leaf.packageName,
          layerName,
        })

        const atPathWithLayer = strippedPackageName
          ? `${layerPathStr}.${strippedPackageName}`
          : layerPathStr
        const atPathWithoutLayer = strippedPackageName ?? ''

        addLeafToTree({
          tree: trees,
          leaf,
          atPath: shouldIncludeLayers ? atPathWithLayer : atPathWithoutLayer,
          nameFromPathPart,
        })
      })
    })
  }

  mergeLeavesByIdInTree(trees)
  sortTreeNodes(trees)

  return trees
}

/**
 * The source information every configuration item version carries and that the tree
 * filters need in order to decide whether the item is visible.
 */
type FilterableOperation = {
  actions: UserActionData[]
  annulOperations: OperationWithSourceInfo<AnnulOperation>[]
}

const isVisibleForApprovalStatus = ({
  operations,
  approvalStatus,
}: {
  operations: FilterableOperation[]
  approvalStatus: FilterStatus['approvalStatus']
}) =>
  operations.some(({ actions }) => isApproved(actions))
    ? approvalStatus.approved
    : approvalStatus.pending

/**
 * The visibility rules shared by every configuration item in the tree: an item whose
 * versions are all annulled is never shown, and the remaining ones have to pass both
 * the item type and the approval status filters.
 *
 * `approvalStatusSources` groups the versions whose approval status is considered. An
 * item is visible when any of the non-empty groups matches the approval status filter,
 * which is what keeps an enum visible through its configured literals. Empty groups are
 * ignored so that an absent source, such as an enum without configured literals, cannot
 * make the item visible on its own.
 */
const isConfigurationItemVisible = ({
  operations,
  filterStatus,
  isTypeVisible,
  approvalStatusSources = [operations],
}: {
  operations: FilterableOperation[]
  filterStatus: Optional<FilterStatus>
  isTypeVisible: (visibleItems: FilterStatus['visibleItems']) => boolean
  approvalStatusSources?: FilterableOperation[][]
}) => {
  const areAllOperationsAnnulled = operations.every(
    ({ annulOperations }) => !!annulOperations.length,
  )

  if (areAllOperationsAnnulled) {
    return false
  }

  if (!filterStatus) {
    return true
  }

  return (
    isTypeVisible(filterStatus.visibleItems) &&
    approvalStatusSources
      .filter((sourceOperations) => sourceOperations.length)
      .some((sourceOperations) =>
        isVisibleForApprovalStatus({
          operations: sourceOperations,
          approvalStatus: filterStatus.approvalStatus,
        }),
      )
  )
}

export const filterTemplateOperation = ({
  isLibrary,
  filterStatus,
  indexedTemplate,
}: {
  indexedTemplate: IndexedTemplateOperations
  isLibrary: boolean
  filterStatus: Optional<FilterStatus>
}) =>
  isConfigurationItemVisible({
    operations: indexedTemplate.operations,
    filterStatus,
    isTypeVisible: (visibleItems) => {
      const isOnlyHiddenTemplate = indexedTemplate.operations.every(
        ({ internalOperation }) => isTemplateHideOperation(internalOperation),
      )

      if (isOnlyHiddenTemplate) {
        return visibleItems.hideTemplate
      }

      return isLibrary ? visibleItems.library : visibleItems.template
    },
  })

/**
 * Parameter definitions, parameter values and the amendments applied on top of them all
 * share the `parameter` item type, so they are filtered the same way.
 */
export const filterParameterOperation = ({
  filterStatus,
  operations,
}: {
  operations: OperationWithSourceInfo<
    ParameterDefinitionCreateOperation | ParameterValueOperation
  >[]
  filterStatus: Optional<FilterStatus>
}) =>
  isConfigurationItemVisible({
    operations,
    filterStatus,
    isTypeVisible: (visibleItems) => visibleItems.parameter,
  })

export const filterEnumDefinition = ({
  filterStatus,
  definition,
}: {
  definition: EnumDefinition
  filterStatus: Optional<FilterStatus>
}) =>
  isConfigurationItemVisible({
    operations: [definition],
    filterStatus,
    isTypeVisible: (visibleItems) => visibleItems.enum,
    approvalStatusSources: [
      [definition],
      definition.literals.filter((literal) => literal.type === 'configured'),
    ],
  })

export const filterGlMapOperation = ({
  filterStatus,
  indexedGlMap,
}: {
  indexedGlMap: IndexedGlMapOperations
  filterStatus: Optional<FilterStatus>
}) =>
  isConfigurationItemVisible({
    operations: indexedGlMap.operations,
    filterStatus,
    isTypeVisible: (visibleItems) =>
      isHideOnlyGlMap(indexedGlMap.operations)
        ? visibleItems.hideGlMap
        : visibleItems.glMap,
  })

export const filterReconciliationSpecOperation = ({
  filterStatus,
  indexedReconciliationSpec,
}: {
  indexedReconciliationSpec: IndexedReconciliationSpecOperations
  filterStatus: Optional<FilterStatus>
}) =>
  isConfigurationItemVisible({
    operations: indexedReconciliationSpec.operations,
    filterStatus,
    isTypeVisible: (visibleItems) => visibleItems.reconciliationSpec,
  })

export const filterRegisteredTenant = ({
  filterStatus,
  registeredTenant,
}: {
  registeredTenant: OperationWithSourceInfo<RegisteredTenantCreateOperation>
  filterStatus: Optional<FilterStatus>
}) =>
  isConfigurationItemVisible({
    operations: [registeredTenant],
    filterStatus,
    isTypeVisible: (visibleItems) => visibleItems.tenant,
  })

export const extractLayerAndPackageFromPath = (path: string[]) => {
  const layerKeys: LayerKey[] = []
  let i = 0
  for (; i < path.length; i++) {
    if (path[i].includes(LAYER_ID_TENANT_SEPARATOR)) {
      layerKeys.push(new LayerKey(path[i]))
    } else {
      break
    }
  }
  return { layerKeys, packageParts: path.slice(i) }
}

export const layerKeyFromPath = (path: string[]): Optional<LayerKey> => {
  const { layerKeys } = extractLayerAndPackageFromPath(path)
  const deepest = layerKeys.at(-1)
  return deepest ? new LayerKey(deepest) : undefined
}

export const extractLocationFromTreeNode = (
  path: string[],
): { layer: LayerKey | undefined; packagePath: string[] } => {
  const { packageParts } = extractLayerAndPackageFromPath(path)
  return {
    layer: layerKeyFromPath(path) ?? undefined,
    packagePath: packageParts,
  }
}

export const resolveCurrentActiveItemFromPath = ({
  activePath,
  nodesByPath,
}: {
  activePath: Optional<string>
  nodesByPath: Map<string, TreeNodeData<ConfigurationTreeLeaf>>
}) => {
  const itemForPath = activePath ? nodesByPath.get(activePath) : undefined
  if (!activePath || !itemForPath) {
    return undefined
  }

  const pathParts = activePath.split('.')
  const { layerKeys, remainingParts } = pathParts.reduce<{
    layerKeys: LayerKey[]
    remainingParts: string[]
  }>(
    (acc, part) => {
      if (part.includes(LAYER_ID_TENANT_SEPARATOR)) {
        acc.layerKeys.push(new LayerKey(part))
      } else {
        acc.remainingParts.push(part)
      }
      return acc
    },
    { layerKeys: [], remainingParts: [] },
  )

  return {
    layers: layerKeys,
    packageName: remainingParts.join('.'),
    item: itemForPath,
  } satisfies ActiveConfigurationItem
}

export const sanitizePackageNameForLayer = ({
  packageName,
  layerName,
}: {
  packageName: Optional<string>
  layerName: string
}) => {
  if (!packageName) {
    return packageName
  }

  const segments = packageName.split('.')

  return segments[0].toLowerCase() === layerName.toLowerCase()
    ? segments.slice(1).join('.') || null
    : packageName
}
